<template>
  <div class="library__article">
    <p>
      <VisibleText>Go to </VisibleText>
      <router-link to="/library">
        <VisibleText>Library</VisibleText>
      </router-link>
    </p>

    <h1 class="library__h1">
      <VisibleText>Trusted AI and Canada’s Public Sector Strategy</VisibleText>
    </h1>

    <div class="library__heading-line"></div>

    <p>
      <VisibleText>2026-05-02</VisibleText>
    </p>

    <img
      class="library__image"
      style="max-height: 15rem; max-width: 95%"
      src="/assets/landing/ai_strategy_public_sector.jpg"
      alt=""
    />

    <h2 class="library__h2">
      <VisibleText>
        Why validation-first architecture is not just a methodology choice — it is a policy
        alignment
      </VisibleText>
    </h2>

    <p>
      <VisibleText>
        Canada’s AI strategy for the federal public service is explicit about what responsible AI
        adoption requires: trust, transparency, and accountability. The Canada School of Public
        Service has made AI literacy a priority across departments. The Munk School of Global
        Affairs and Public Policy, in collaboration with federal partners, has brought senior public
        servants together to examine what AI-ready government actually looks like. The question
        being asked in deputy minister offices, central agencies, and program teams across the
        country is increasingly the same: how do we adopt AI in a way that we can stand behind?
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        The answer is not a governance checklist. It is an architectural choice. Validation-first AI
        — statistical rigor before generative synthesis, evidence before narrative, auditability
        built into the analytical process rather than bolted on afterward — is not just the right
        methodology for public sector analysis. It is the only methodology that is structurally
        consistent with what responsible AI adoption in government actually requires.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText
        >What Canada’s AI Strategy Requires — and What Most Tools Cannot Deliver</VisibleText
      >
    </h2>

    <p>
      <VisibleText>
        The Government of Canada’s approach to AI in the public service rests on five core
        principles that appear consistently across federal guidance, Treasury Board policy, and the
        emerging practice of departments that have moved beyond pilot projects: trustworthiness,
        transparency, accountability, sovereignty, and proportionality.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Each principle has a direct implication for the analytical tools government organizations
        use — and for why the dominant generation-first AI platforms currently in the market cannot
        fully satisfy them.
      </VisibleText>
    </p>

    <table>
      <thead>
        <tr>
          <th>Principle</th>
          <th>What It Demands of Analytical Tools</th>
          <th>How Validation-First Delivers It</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Trustworthiness</td>
          <td>
            Findings must be grounded in evidence that can be verified, not inferred from AI
            pattern-matching that cannot be inspected.
          </td>
          <td>
            Every theme, frequency, and driver relationship is produced by a validated dictionary
            applied consistently to the full dataset. No finding exists that cannot be traced to
            specific responses.
          </td>
        </tr>

        <tr>
          <td>Transparency</td>
          <td>
            The methodology must be explainable to non-technical audiences, including Parliament,
            audit offices, and citizens who ask.
          </td>
          <td>
            The ResearchAI dictionary is visible, editable, and auditable. The logic behind every
            classification can be explained in plain language without reference to model weights or
            inference probabilities.
          </td>
        </tr>

        <tr>
          <td>Accountability</td>
          <td>
            Analysis used in public decisions must be reproducible. Running the same analysis on the
            same data must produce the same result.
          </td>
          <td>
            Dictionary-based validation is deterministic. Same inputs, same outputs, every time.
            GenAI is used only after validation — for synthesis and communication, not for
            discovery.
          </td>
        </tr>

        <tr>
          <td>Sovereignty</td>
          <td>
            Citizen and employee data must remain under Canadian control, within Canadian
            infrastructure, protected from foreign AI training pipelines.
          </td>
          <td>
            ResearchAI is a Canadian platform. Data does not leave Canadian infrastructure. Client
            data is never used to train models. Architectural sovereignty, not contractual
            mitigation.
          </td>
        </tr>

        <tr>
          <td>Proportionality</td>
          <td>
            Tools must be accessible to small teams with bounded budgets, not only to departments
            with enterprise analytics capacity.
          </td>
          <td>
            Unlimited-user licensing, proof-of-concept pricing, and a capability transfer model that
            eliminates ongoing dependency costs. Designed for government procurement reality.
          </td>
        </tr>
      </tbody>
    </table>

    <h2 class="library__h2">
      <VisibleText>Three Levels of Government, One Common Requirement</VisibleText>
    </h2>

    <p>
      <VisibleText>
        The case studies in this series span three levels of Canadian government and one
        international context. What they share is not a sector, a service type, or a scale of
        operation. What they share is the accountability environment that all public sector
        organizations operate in — and the analytical requirements that environment creates.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        At the federal level, ESDC and Service Canada have demonstrated that validation-first text
        analytics can support policy analysis, citizen feedback intelligence, and program evaluation
        at scale — with outputs that have been recognized with a Government of Canada Outstanding
        Innovation Award. The Canada Disability Benefit launch required analysis that was defensible
        under ministerial scrutiny. Workslop was not an option.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        At the provincial and territorial level, the Government of Yukon built citizen intelligence
        infrastructure from a standing start, with a lean team, on a territorial government budget,
        meeting data sovereignty requirements that most commercial AI platforms cannot satisfy. The
        Internal Services case demonstrates that the same methodology applies with equal force to
        the employee-facing side of government — shared corporate services that are invisible in
        most performance reporting but whose failures carry direct public service delivery
        consequences.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        At the local and municipal level, GovMetric’s partnership with ResearchAI brings
        validation-first citizen feedback intelligence to more than 100 UK local councils —
        organizations with even more constrained analytical capacity than their Canadian
        counterparts, and with an equally pressing need for evidence that can support service
        improvement decisions in a public accountability context.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        "Trusted AI isn’t a policy aspiration. It is an architectural choice that either gets made
        at the design stage or compensated for, at much greater cost, afterward."
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>The ICCS Connection: Citizens First, Analytically</VisibleText>
    </h2>

    <p>
      <VisibleText>
        The Institute for Citizen-Centred Service and its Citizens First research program have
        provided Canadian public sector organizations with a validated framework for understanding
        citizen expectations and measuring service quality for more than two decades. The Common
        Measurements Tool reflects accumulated knowledge about what citizens value in their
        interactions with government — timeliness, outcome, fairness, knowledge, courtesy, access —
        and provides a standardized vocabulary for tracking performance across organizations and
        over time.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        ResearchAI’s integration with the CMT framework extends this measurement capability into the
        domain of unstructured feedback — the qualitative intelligence that structured surveys
        cannot capture. The Failure Demand frameworks developed for the Government of Yukon, the
        Internal Services engagement, and the social media analysis in the DMV case all map to CMT
        dimensions, making it possible to connect unstructured citizen signal directly to the
        measurement vocabulary Canadian government has been building for twenty years.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        This integration matters because it means ResearchAI-generated insights do not require
        organizations to choose between their existing measurement architecture and new analytical
        capability. The two reinforce each other. The CMT survey tells you how citizens scored their
        experience. The ResearchAI Failure Demand analysis tells you why — and what the system is
        doing to generate demand that could be eliminated entirely.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>The Road Ahead: What Comes After the Foundation Is Built</VisibleText>
    </h2>

    <p>
      <VisibleText>
        Every organization that has worked through the ResearchAI Value Creation cycle arrives at
        the same position at the end of the first engagement: they have a baseline. They know what
        their data contains, what their failure patterns look like, and what the gap is between the
        service experience they think they are delivering and the one their citizens or employees
        are actually having.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        That baseline is the most valuable analytical asset a public sector organization can build.
        Not because of what it shows in isolation, but because of what it makes possible in every
        subsequent cycle. Improvement becomes measurable. Actions become testable. The feedback loop
        closes and compounds. The organization develops what no external audit, no periodic
        satisfaction survey, and no generation-first AI tool can provide: the continuous, validated,
        audit-ready intelligence that genuine service improvement requires.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Canada’s public sector is at an inflection point in its relationship with AI. The
        organizations that will lead are not the ones that adopt AI most rapidly. They are the ones
        that adopt it in the right order — building the statistical foundation before extending with
        generative capability, earning the trust that allows AI-assisted analysis to be used in
        public decisions without reservation.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        The tools exist. The methodology is proven across federal, provincial, territorial, and
        international contexts. The alignment with Canada’s AI strategy, the ICCS framework, and the
        accountability requirements of public sector governance is structural, not incidental.
      </VisibleText>
    </p>

    <p>
      <VisibleText>The only remaining question is where to start.</VisibleText>
    </p>

    <h3>
      <VisibleText>About ResearchAI</VisibleText>
    </h3>

    <p>
      <VisibleText>
        ResearchAI (research-ai.io) is a Canadian text analytics platform developed by PigeonLine
        Ltd. It combines traditional data science with strategically applied GenAI to transform
        unstructured feedback into validated organizational intelligence. Used by federal,
        provincial, and territorial governments across Canada, and by public sector partners in the
        UK and internationally.
      </VisibleText>
    </p>

    <div class="library__textbox" style="padding: 0.5em">
      <div class="library__textbox__col">
        <img
          class="library__image library__image--nonresponsive"
          style="max-width: 4rem"
          src="/assets/landing/analyze_icon.png"
          alt=""
        />
      </div>
      <div class="library__textbox__col">
        <ul>
          <li>
            <VisibleText>
              If you are an existing user,
              <router-link to="/login">login to ResearchAI</router-link>
              and try something new. Or reach out to us at:
              <a href="mailto:hello@researchai.io">hello@researchai.io</a>
            </VisibleText>
          </li>
          <li>
            <VisibleText>
              If you are finding us for the first time,
              <router-link to="/register">register for a free ResearchAI account.</router-link>
              We will be in touch to set you up for success.
            </VisibleText>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue';
import { computed, reactive } from 'vue';

export default {
  name: 'TrustedAIPublicSector',

  setup() {
    const siteData = reactive({
      title: 'The Trusted AI Series · Trusted AI and Canada’s Public Sector Strategy',
      description:
        'Why validation-first architecture is not just a methodology choice — it is a policy alignment.',
    });

    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
      ],
    });

    return { siteData };
  },

  methods: {
    goto(refName) {
      this.$refs[refName].scrollIntoView();
    },
  },
};
</script>
